<template>
  <carousel
    v-if="renderNow"
    class="product-box-slider product-box-slider__slider recommendations-tiles-teaser__products"
    :class="`product-box-slider--itemcount_${listItems.length}`"
    :slides-count="listItems.length"
    :options="swiperOptions"
    :preload-slide-count="1"
    :show-skip-slider-link="true"
  >
    <template
      v-for="(product, index) in listItems"
      :key="product.id"
      #[`slide-${index}`]
    >
      <component
        :is="productBoxComponent"
        class="product-box-slider__item"
        :product-box-data="product"
        :hide-counter-button="hideCartButton"
        :tracking-additional="{
          position: index,
          list: trackingList
        }"
        :show-review="showReview"
        :state="getProductState(product)"
        @addToBasket="addToBasketHandler"
        @quantityChange="quantityChange"
        @addToWishlist="addToWishlistHandler"
        @removeFromWishlist="removeFromWishlistHandler"
      />
    </template>
  </carousel>
</template>

<script lang="ts">
  import {Options, Prop} from 'vue-property-decorator';
  import ProductBox from '../productBox/productBox.vue';
  import ProductBoxWide from '../productBoxWide/productBoxWide.vue';
  import {productBoxContainerMixin, ProductBoxContainerClass} from '../../utilities/vueMixins/productBoxContainerMixin/productBoxContainerMixin';
  import Carousel from '../../components/carousel/carousel.vue';
  import {breakpoints} from '../../../bps/variables/variables.js';
  import {calculateHasTooFewItems, calculateSlidesPerView} from '../../utilities/js/truncateSlidesInView/truncateSlidesInView';
  import ProductBoxData from '../../types/ProductBoxData';
  import {ProductBoxSettings} from '../productBox/productBoxSettings';
  import diContainer from '../../utilities/js/diContainer/diContainer';
  import {usePlaceholder} from '../../utilities/vueMixins/placeholderMixin/placeholder';

  @Options({
    name: 'product-box-slider',
    components: {
      Carousel, ProductBox, ProductBoxWide
    },
    mixins: [productBoxContainerMixin]
  })
  export default class ProductBoxSlider extends ProductBoxContainerClass {
    @Prop({default: false})
    delayRender!: boolean;

    @Prop({default: 4})
    columns: number;

    @Prop({default: false})
    showReview: boolean;

    @Prop()
    slidesPerViewLaptop: number;
    @Prop()
    slidesPerViewTabletLandscape: number;
    @Prop()
    slidesPerViewTablet: number;
    @Prop()
    slidesPerViewMobile: number;
    @Prop()
    placeholderId: string;

    settings: ProductBoxSettings = diContainer.get('productBoxSettings');
    renderNow = !this.delayRender;
    isMobile: boolean = null;

    mounted() {
      // For carousels in tabs: width was calculated too early, rendering must be delayed
      if (this.renderNow === false) {
        this.$nextTick(() => {
          this.renderNow = true;
        });
      }

      window.addEventListener('breakpointChange', this.breakpointChangeHandler.bind(this));
      this.breakpointChangeHandler();
      usePlaceholder(this.placeholderId || 'product-box-slider');
    }

    get swiperOptions() {
      return {
        breakpoints: {
          [0]: {
            slidesPerView: this.slidesPerViewMobile || this.getSlidesPerView(-3, !this.hasTooFewItems(-3), 'mobile'),
            allowTouchMove: !this.hasTooFewItems(-3),
            spaceBetween: 10,
            slidesPerGroup: 1,
            centeredSlides: this.settings.slider.mobile.centeredSlides,
            showPagination: true,
          },
          [breakpoints.phone]: {
            slidesPerView: this.slidesPerViewMobile || this.getSlidesPerView(-3, !this.hasTooFewItems(-3), 'mobile'),
            allowTouchMove: !this.hasTooFewItems(-3),
            spaceBetween: 16,
            slidesPerGroup: 1,
            centeredSlides: this.settings.slider.mobile.centeredSlides,
            showPagination: true,
          },
          [breakpoints.tabletSm]: {
            slidesPerView: this.slidesPerViewMobile || this.getSlidesPerView(-2, !this.hasTooFewItems(-2), 'tablet'),
            allowTouchMove: !this.hasTooFewItems(-2),
            spaceBetween: 16,
            slidesPerGroup: 1,
            centeredSlides: this.settings.slider.mobile.centeredSlides,
            showPagination: true,
          },
          [breakpoints.tablet]: {
            slidesPerView: this.slidesPerViewTablet || this.getSlidesPerView(-2, !this.hasTooFewItems(-2), 'tablet'),
            allowTouchMove: !this.hasTooFewItems(-2),
            spaceBetween: 24,
            slidesPerGroup: Math.max(this.columns - 2, 1),
            centeredSlides: this.settings.slider.tablet.centeredSlides,
            showPagination: false,
          },
          [breakpoints.tablet + 112]: {
            slidesPerView: this.slidesPerViewTabletLandscape || this.getSlidesPerView(-1),
            allowTouchMove: !this.hasTooFewItems(-1),
            spaceBetween: 24,
            slidesPerGroup: Math.max(this.columns - 1, 1),
            centeredSlides: this.settings.slider.tablet.centeredSlides,
          },
          [breakpoints.laptopLg]: {
            slidesPerView: this.slidesPerViewLaptop || this.getSlidesPerView(0),
            allowTouchMove: !this.hasTooFewItems(0),
            spaceBetween: 24,
            slidesPerGroup: this.columns,
            centeredSlides: this.settings.slider.desktop.centeredSlides,
          }
        }
      }
    }

    getSlidesPerView(breakpointMod: number, useOffset: boolean = false, breakpoint: string = null): number {
      if (useOffset && breakpoint) {
        return Math.min(
          calculateSlidesPerView(breakpointMod, this.columns, this.products.length, useOffset, this.settings.slider[breakpoint].offset),
          this.listItems.length
        );
      }
      return Math.min(
        calculateSlidesPerView(breakpointMod, this.columns, this.products.length, useOffset),
        this.listItems.length
      );
    }

    hasTooFewItems(breakpointMod: number): boolean {
      return calculateHasTooFewItems(breakpointMod, this.columns, this.products.length);
    }

    get productBoxComponent(): 'ProductBoxWide'|'ProductBox' {
      return !this.isMobile && this.products.length === 1 ? 'ProductBoxWide' : 'ProductBox';
    }

    breakpointChangeHandler() {
      this.isMobile = window.innerWidth < breakpoints.tablet;
    }

    quantityChange(product: ProductBoxData, quantity: number) {
      this.quantityChangeHandler(product, quantity);
    }
  }
</script>
